import type { ComponentType, CSSProperties } from 'react';
import {
  AlertOutlined,
  ApartmentOutlined,
  BankOutlined,
  BarChartOutlined,
  BookOutlined,
  CodeOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DollarOutlined,
  DownloadOutlined,
  FileSearchOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  FlagOutlined,
  HomeOutlined,
  MailOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  RiseOutlined,
  RobotOutlined,
  SettingOutlined,
  SafetyOutlined,
  TagOutlined,
  TeamOutlined,
  TrophyOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  StopOutlined,
  UserDeleteOutlined,
  UserOutlined,
  EyeOutlined,
} from '@ant-design/icons';

export type WorkspaceId =
  | 'growth'
  | 'trust-moderation'
  | 'data-operations'
  | 'ai-coach'
  | 'platform-engineering';

export type WorkspaceIcon = ComponentType<{ size?: number; style?: CSSProperties }>;

export interface WorkspaceNavItem {
  id: string;
  label: string;
  path: string;
  icon: WorkspaceIcon;
  requiredPermission?: string;
}

export interface WorkspaceNavGroup {
  id: string;
  label: string;
  /**
   * Optional section-level dashboard rendered above group items with elevated
   * hierarchy (stronger weight, no indent) relative to operational sub-items.
   */
  dashboard?: WorkspaceNavItem;
  items: WorkspaceNavItem[];
}

export interface WorkspaceDefinition {
  id: WorkspaceId;
  label: string;
  description: string;
  homePath: string;
  icon: WorkspaceIcon;
  /**
   * Optional pinned items rendered above `overview` (no section header).
   * Used when a workspace needs Analytics Overview + operational Dashboard.
   */
  pinnedAbove?: WorkspaceNavItem[];
  /** Pinned below the role switcher — no section header */
  overview: WorkspaceNavItem;
  groups: WorkspaceNavGroup[];
}

export const WORKSPACE_STORAGE_KEY = 'scoutus.admin.activeWorkspace';

export const WORKSPACES: WorkspaceDefinition[] = [
  {
    id: 'growth',
    label: 'Growth & Business Ops',
    description: 'Track growth, revenue, and partner funnel',
    homePath: '/v2/growth',
    icon: RiseOutlined,
    overview: {
      id: 'overview',
      label: 'Overview',
      icon: DashboardOutlined,
      path: '/v2/growth',
      requiredPermission: 'analytics:read',
    },
    groups: [
      {
        id: 'analytics',
        label: 'Analytics',
        items: [
          {
            id: 'acquisition-growth-hub',
            label: 'Acquisition & Growth',
            icon: RiseOutlined,
            path: '/v2/analytics',
            requiredPermission: 'analytics:read',
          },
          {
            id: 'product-engagement-hub',
            label: 'Product Engagement',
            icon: DashboardOutlined,
            path: '/v2/analytics/engagement',
            requiredPermission: 'analytics:read',
          },
          {
            id: 'revenue-marketing-hub',
            label: 'Revenue & Marketing',
            icon: DollarOutlined,
            path: '/v2/analytics/monetization',
            requiredPermission: 'analytics:read',
          },
        ],
      },
      {
        id: 'organizations',
        label: 'Organizations',
        items: [
          {
            id: 'organization-accounts',
            label: 'Organization Accounts',
            icon: ApartmentOutlined,
            path: '/v2/organizations',
            requiredPermission: 'users:read',
          },
        ],
      },
      {
        id: 'recruiters-partners',
        label: 'Recruiters & Partners',
        items: [
          { id: 'glossary', label: 'Community Glossary', icon: BookOutlined, path: '/v2/glossary', requiredPermission: 'settings:manage' },
        ],
      },
    ],
  },
  {
    id: 'trust-moderation',
    label: 'Trust & Moderation',
    description: 'Approve accounts, review queues, and handle safety',
    homePath: '/v2/trust/overview',
    icon: SafetyOutlined,
    overview: {
      id: 'overview',
      label: 'Overview',
      icon: BarChartOutlined,
      path: '/v2/trust/overview',
    },
    groups: [
      {
        id: 'account-approvals',
        label: 'Account Approvals',
        dashboard: {
          id: 'account-dashboard',
          label: 'Account Dashboard',
          icon: DashboardOutlined,
          path: '/v2/trust/accounts',
          requiredPermission: 'users:read',
        },
        items: [
          { id: 'coaches', label: 'Coaches & Staff', icon: UserOutlined, path: '/v2/manage-coach', requiredPermission: 'users:read' },
          { id: 'scouts', label: 'Scouts & Evaluators', icon: EyeOutlined, path: '/v2/manage-scout', requiredPermission: 'users:read' },
          { id: 'brand-recruiters', label: 'Brand Recruiters', icon: TagOutlined, path: '/v2/manage-brand-recruiters', requiredPermission: 'users:read' },
        ],
      },
      {
        id: 'moderation',
        label: 'Moderation',
        dashboard: {
          id: 'moderation-dashboard',
          label: 'Moderation Dashboard',
          icon: DashboardOutlined,
          path: '/v2/trust/moderation',
          requiredPermission: 'moderation:read',
        },
        items: [
          { id: 'reports', label: 'Reports', icon: FlagOutlined, path: '/v2/moderation/reports', requiredPermission: 'moderation:read' },
          { id: 'text-appeals', label: 'Appeals', icon: FileSearchOutlined, path: '/v2/moderation/text-appeals', requiredPermission: 'moderation:read' },
          { id: 'enforcement-ruleset', label: 'Rules', icon: UnorderedListOutlined, path: '/v2/trust/moderation/ruleset', requiredPermission: 'moderation:read' },
        ],
      },
      {
        id: 'user-repository',
        label: 'User Repository',
        items: [
          { id: 'banned-suspended', label: 'Bans & Suspensions', icon: StopOutlined, path: '/v2/bans-suspensions', requiredPermission: 'settings:manage' },
          { id: 'deleted-users', label: 'Deleted & Deactivated', icon: UserDeleteOutlined, path: '/v2/deleted-deactivated-users', requiredPermission: 'settings:manage' },
        ],
      },
      {
        id: 'policy-rules',
        label: 'Policy & Rules',
        items: [
          { id: 'glossary', label: 'Community Glossary', icon: BookOutlined, path: '/v2/glossary', requiredPermission: 'settings:manage' },
        ],
      },
    ],
  },
  {
    id: 'data-operations',
    label: 'Data Operations',
    description: 'Manage directories, schools, imports, and stats',
    homePath: '/v2/data',
    icon: DatabaseOutlined,
    overview: {
      id: 'overview',
      label: 'Overview',
      icon: DashboardOutlined,
      path: '/v2/data',
      requiredPermission: 'admin:manage',
    },
    groups: [
      {
        id: 'athletic-directories',
        label: 'Athletic Directories',
        items: [
          { id: 'athletes', label: 'Athletes Directory', icon: TeamOutlined, path: '/v2/manage-athlete', requiredPermission: 'users:read' },
          { id: 'parents', label: 'Parents Roster', icon: HomeOutlined, path: '/v2/manage-parents', requiredPermission: 'users:read' },
        ],
      },
      {
        id: 'institutional-data',
        label: 'Institutional Data',
        items: [
          { id: 'colleges', label: 'Colleges & Universities', icon: BankOutlined, path: '/v2/manage-college', requiredPermission: 'admin:manage' },
          { id: 'athletic-programs', label: 'Athletic Programs', icon: TrophyOutlined, path: '/v2/athletic-programs', requiredPermission: 'admin:manage' },
          { id: 'athletic-program-lookup', label: 'CSV School Uploader', icon: ApartmentOutlined, path: '/v2/athletic-program-lookup', requiredPermission: 'admin:manage' },
          { id: 'coach-import', label: 'Batch Processing', icon: UploadOutlined, path: '/v2/coach-import', requiredPermission: 'admin:manage' },
        ],
      },
      {
        id: 'stats-verification',
        label: 'Stats & Verification',
        items: [
          { id: 'sport-stats', label: 'Sport Stats Registry', icon: ProfileOutlined, path: '/v2/manage-sport-stats', requiredPermission: 'admin:manage' },
          { id: 'all-stats', label: 'All-Time Stats', icon: UnorderedListOutlined, path: '/v2/manage-all-stats', requiredPermission: 'admin:manage' },
        ],
      },
    ],
  },
  {
    id: 'ai-coach',
    label: 'AI Coach',
    description: 'Configure the coach and test its answers',
    homePath: '/v2/ai-coach',
    icon: RobotOutlined,
    overview: {
      id: 'overview',
      label: 'Overview',
      icon: DashboardOutlined,
      path: '/v2/ai-coach',
      requiredPermission: 'admin:manage',
    },
    groups: [
      {
        id: 'coach-configuration',
        label: 'Coach Configuration',
        items: [
          { id: 'ai-library', label: 'Knowledge Library', icon: BookOutlined, path: '/v2/ai-coach/library', requiredPermission: 'admin:manage' },
          { id: 'ai-coaches', label: 'Coach Personas', icon: UserOutlined, path: '/v2/ai-coach/coaches', requiredPermission: 'admin:manage' },
          { id: 'ai-guidance', label: 'Coach Instructions', icon: FileTextOutlined, path: '/v2/ai-coach/guidance', requiredPermission: 'admin:manage' },
          { id: 'ai-gaps', label: 'Unanswered Topics', icon: AlertOutlined, path: '/v2/ai-coach/gaps', requiredPermission: 'admin:manage' },
        ],
      },
      {
        id: 'testing',
        label: 'Testing',
        items: [
          { id: 'ai-questions', label: 'Test Questions', icon: QuestionCircleOutlined, path: '/v2/ai-coach/questions', requiredPermission: 'admin:manage' },
          { id: 'ai-test-runs', label: 'Test Runs', icon: ExperimentOutlined, path: '/v2/ai-coach/test-runs', requiredPermission: 'admin:manage' },
        ],
      },
      {
        id: 'monitoring',
        label: 'Monitoring',
        items: [
          { id: 'ai-usage', label: 'Usage & Cost', icon: DollarOutlined, path: '/v2/ai-coach/usage', requiredPermission: 'admin:manage' },
        ],
      },
    ],
  },
  {
    id: 'platform-engineering',
    label: 'Platform & Engineering',
    description: 'Configure flags, settings, templates, and AI',
    homePath: '/v2/platform',
    icon: CodeOutlined,
    overview: {
      id: 'overview',
      label: 'Overview',
      icon: DashboardOutlined,
      path: '/v2/platform',
      requiredPermission: 'settings:manage',
    },
    groups: [
      {
        id: 'configuration',
        label: 'Configuration',
        items: [
          { id: 'feature-flags', label: 'Feature Flags', icon: FlagOutlined, path: '/v2/feature-flags', requiredPermission: 'settings:manage' },
          { id: 'system-settings', label: 'System Settings', icon: SettingOutlined, path: '/v2/system-settings', requiredPermission: 'settings:manage' },
        ],
      },
      {
        id: 'templates-exports',
        label: 'Templates & Exports',
        items: [
          { id: 'templates', label: 'Email & Push Templates', icon: MailOutlined, path: '/v2/manage-templates', requiredPermission: 'settings:manage' },
          { id: 'templates-export', label: 'Template Export Engine', icon: DownloadOutlined, path: '/v2/templates-export', requiredPermission: 'settings:manage' },
        ],
      },
      {
        id: 'ai-coach',
        label: 'AI Coach',
        items: [
          { id: 'ai-overview', label: 'Overview', icon: DashboardOutlined, path: '/v2/ai-coach', requiredPermission: 'admin:manage' },
          { id: 'ai-library', label: 'Library', icon: RobotOutlined, path: '/v2/ai-coach/library', requiredPermission: 'admin:manage' },
          { id: 'ai-gaps', label: 'Gaps', icon: AlertOutlined, path: '/v2/ai-coach/gaps', requiredPermission: 'admin:manage' },
          { id: 'ai-coaches', label: 'Coaches', icon: UserOutlined, path: '/v2/ai-coach/coaches', requiredPermission: 'admin:manage' },
          { id: 'ai-guidance', label: 'Guidance', icon: FileTextOutlined, path: '/v2/ai-coach/guidance', requiredPermission: 'admin:manage' },
          { id: 'ai-questions', label: 'Test Questions', icon: QuestionCircleOutlined, path: '/v2/ai-coach/questions', requiredPermission: 'admin:manage' },
          { id: 'ai-test-runs', label: 'Test Runs', icon: ExperimentOutlined, path: '/v2/ai-coach/test-runs', requiredPermission: 'admin:manage' },
        ],
      },
    ],
  },
];

export const DEFAULT_WORKSPACE_ID: WorkspaceId = 'growth';

export function getWorkspaceById(id: WorkspaceId): WorkspaceDefinition {
  return WORKSPACES.find((workspace) => workspace.id === id) ?? WORKSPACES[0];
}

export function findWorkspaceIdForPath(pathname: string): WorkspaceId | null {
  const normalized = pathname.startsWith('/v2') ? pathname : `/v2${pathname}`;

  let bestMatch: { id: WorkspaceId; length: number } | null = null;

  for (const workspace of WORKSPACES) {
    const pinnedPaths = [
      ...(workspace.pinnedAbove ?? []).map((item) => item.path),
      workspace.overview.path,
    ];
    for (const pinnedPath of pinnedPaths) {
      if (
        normalized === pinnedPath ||
        normalized.startsWith(`${pinnedPath}/`)
      ) {
        if (!bestMatch || pinnedPath.length > bestMatch.length) {
          bestMatch = { id: workspace.id, length: pinnedPath.length };
        }
      }
    }

    for (const group of workspace.groups) {
      const groupPaths = [
        ...(group.dashboard ? [group.dashboard.path] : []),
        ...group.items.map((item) => item.path),
      ];
      for (const itemPath of groupPaths) {
        if (
          normalized === itemPath ||
          normalized.startsWith(`${itemPath}/`)
        ) {
          if (!bestMatch || itemPath.length > bestMatch.length) {
            bestMatch = { id: workspace.id, length: itemPath.length };
          }
        }
      }
    }
  }

  return bestMatch?.id ?? null;
}

export function isWorkspaceNavItemActive(pathname: string, itemPath: string): boolean {
  const normalized = pathname.startsWith('/v2') ? pathname : `/v2${pathname}`;
  if (itemPath === '/v2/analytics') {
    return normalized === '/v2/analytics' || normalized === '/v2/analytics/' || normalized === '/v2/dashboard' || normalized === '/v2/dashboard/';
  }
  if (itemPath === '/v2/platform') {
    return normalized === '/v2/platform' || normalized === '/v2/platform/';
  }
  if (itemPath === '/v2/growth') {
    return normalized === '/v2/growth' || normalized === '/v2/growth/';
  }
  if (itemPath === '/v2/trust/overview') {
    return normalized === '/v2/trust/overview' || normalized === '/v2/trust/overview/';
  }
  if (itemPath === '/v2/trust/accounts') {
    return normalized === '/v2/trust/accounts' || normalized === '/v2/trust/accounts/';
  }
  if (itemPath === '/v2/trust/moderation') {
    return normalized === '/v2/trust/moderation' || normalized === '/v2/trust/moderation/';
  }
  if (itemPath === '/v2/data') {
    return normalized === '/v2/data' || normalized === '/v2/data/';
  }
  if (itemPath === '/v2/manage-media' || itemPath === '/v2/moderation/media') {
    return normalized.startsWith('/v2/moderation/media') || normalized.startsWith('/v2/manage-media');
  }
  return normalized === itemPath || normalized.startsWith(`${itemPath}/`);
}

export interface NavPathContext {
  workspaceId: WorkspaceId;
  workspaceLabel: string;
  groupLabel: string | null;
  pageLabel: string;
  path: string;
}

/** Best-matching nav item for breadcrumbs / header context. */
export function findNavContextForPath(pathname: string): NavPathContext | null {
  const normalized = pathname.startsWith('/v2') ? pathname : `/v2${pathname}`;
  const candidates: NavPathContext[] = [];

  const matchesPath = (path: string) =>
    normalized === path ||
    normalized.startsWith(`${path}/`) ||
    isWorkspaceNavItemActive(normalized, path);

  for (const workspace of WORKSPACES) {
    for (const pinned of workspace.pinnedAbove ?? []) {
      if (matchesPath(pinned.path)) {
        candidates.push({
          workspaceId: workspace.id,
          workspaceLabel: workspace.label,
          groupLabel: null,
          pageLabel: pinned.label,
          path: pinned.path,
        });
      }
    }
    if (matchesPath(workspace.overview.path)) {
      candidates.push({
        workspaceId: workspace.id,
        workspaceLabel: workspace.label,
        groupLabel: null,
        pageLabel: workspace.overview.label,
        path: workspace.overview.path,
      });
    }
    for (const group of workspace.groups) {
      const groupItems = [
        ...(group.dashboard ? [group.dashboard] : []),
        ...group.items,
      ];
      for (const item of groupItems) {
        if (matchesPath(item.path)) {
          candidates.push({
            workspaceId: workspace.id,
            workspaceLabel: workspace.label,
            groupLabel: group.label,
            pageLabel: item.label,
            path: item.path,
          });
        }
      }
    }
  }

  if (candidates.length === 0) return null;
  return candidates.reduce((best, candidate) =>
    candidate.path.length > best.path.length ? candidate : best,
  );
}

